import React, { useEffect, useState } from 'react';
import 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetShareProgrammation } from './useGetShareProgrammation';
import { NotFoundPage } from 'components/ErrorPage/NotFoundPage';
import { TryTeetshHeader } from 'components/TryTeetshHeader';
import Logo from 'assets/logo-white.png';
import FakeUserProvider from 'containers/App/context/FakeUserProvider';
import { FakeSchoolSchoolyearProvider } from 'containers/App/context/FakeSchoolSchoolyearProvider';
import { Provider } from 'jotai';
import { useHydrateAtoms } from 'jotai/react/utils';
import {
  programmationIdAtom,
  programmationIsV2Atom,
  programmationModeAtom,
  useProg,
  useSetToPreviewMode,
} from 'containers/Programmations/atoms/progAtoms';
import { schoolyearSchoolAtom } from 'containers/App/context/atom';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'components/Resizable';
import { PrintView } from '../Print/types';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import { Sidebar } from './Sidebar';
import { Body } from './Body';
import { PrintConfig } from '../Preview/types';

const HydrateAtoms = ({ initialValues, children }) => {
  useHydrateAtoms(initialValues);
  return children;
};

interface Props {
  periodes: LabelledDateRange[];
  view: PrintView;
  token: string;
}

function ProgV2Share(props: Props) {
  const { periodes, view } = props;
  const { guestMode } = useUser();
  const prog = useProg();
  const [printConfig, setPrintConfig] = useState<PrintConfig>({
    groupWeeksByPeriodes: true,
    matiereIds: prog?.matieres.map(m => m.id) || [],
    rowTitleOrientation: 'vertical',
    preventCutting: true,
    breakBefore: false,
    view,
  });
  if (!guestMode || !prog) return null;

  return (
    <div tw="flex flex-col h-full overflow-hidden w-full">
      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="carnet-eleve-sidebar"
      >
        <Sidebar
          printConfig={printConfig}
          onUpdatePrintConfig={setPrintConfig}
          token={props.token}
        />
        <ResizableHandle />
        <ResizablePanel order={1}>
          <div tw="overflow-auto h-full bg-neutral-quiet">
            <Body printConfig={printConfig} periodes={periodes} />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export function ProgV2ShareWrapper() {
  const params = useParams<{
    token: string;
    userId: string;
  }>();
  const userId = params.userId as string;
  const token = params.token as string;
  const { t } = useTranslation(['common']);
  const { data, error } = useGetShareProgrammation(userId, token);
  const setToPreviewMode = useSetToPreviewMode();
  useEffect(() => {
    setToPreviewMode();
  }, []);

  if (error?.response?.status === 404) {
    return <NotFoundPage title={t('common:messages.genericError')} />;
  }
  if (!data) return null;

  return (
    <div tw="flex flex-col max-h-screen overflow-hidden">
      <div className="dark">
        <TryTeetshHeader
          utmCampaign="share_programmation"
          title={<img style={{ height: '30px' }} alt="Teetsh" src={Logo} />}
        />
      </div>
      <FakeUserProvider
        user={{
          id: userId,
          location: data.tz,
          locale: data.locale,
          country: data.country,
          guestMode: {
            enabled: true,
            permission: data.permission,
          },
        }}
      >
        <Provider>
          <FakeSchoolSchoolyearProvider
            schoolyear={data.schoolyear}
            school={data.school}
          >
            <HydrateAtoms
              initialValues={[
                [programmationIdAtom, data.programmationId],
                [programmationIsV2Atom, true],
                [programmationModeAtom, true],
                [
                  schoolyearSchoolAtom,
                  {
                    schoolId: data.school?.id,
                    schoolyearId: data.schoolyear?.id,
                  },
                ],
              ]}
            >
              <ProgV2Share
                periodes={data.schoolyear.zone?.periodes || []}
                view={data.options.programmationDefaultView}
                token={token}
              />
            </HydrateAtoms>
          </FakeSchoolSchoolyearProvider>
        </Provider>
      </FakeUserProvider>
    </div>
  );
}
